/**
 * Centralized logout / session cleanup for Helpdesk frontend.
 */
import MessagingService from './MessagingServices';
import authService from '../api/authService';
import { queryClient } from '../lib/queryClient';
import { clearRealtimeNotificationToastQueue } from '../utils/realtimeNotificationToast';
import {
  isSessionTakeoverInProgress,
  isPostTakeoverGraceActive,
  clearPostTakeoverGrace,
} from './sessionTakeoverGuard';

let logoutInProgress = false;

/** @type {{ setUser: ((v: null) => void) | null, setError: ((v: null) => void) | null }} */
let authHandlers = { setUser: null, setError: null };

/** @type {{ clearAllNotifications: (() => void) | null }} */
let notificationHandlers = { clearAllNotifications: null };

/**
 * Register AuthContext setters (called from AuthProvider mount).
 * @param {{ setUser?: (v: null) => void, setError?: (v: null) => void } | null} handlers
 */
export function registerAuthLogoutHandlers(handlers) {
  if (!handlers) {
    authHandlers = { setUser: null, setError: null };
    return;
  }
  authHandlers = {
    setUser: handlers.setUser ?? null,
    setError: handlers.setError ?? null,
  };
}

/**
 * Register NotificationContext clear function (called from NotificationProvider mount).
 * @param {{ clearAllNotifications?: () => void } | null} handlers
 */
export function registerNotificationLogoutHandlers(handlers) {
  if (!handlers) {
    notificationHandlers = { clearAllNotifications: null };
    return;
  }
  notificationHandlers = {
    clearAllNotifications: handlers.clearAllNotifications ?? null,
  };
}

export function isLogoutInProgress() {
  return logoutInProgress;
}

/**
 * Full logout sequence used for manual logout, session conflict, 401, and token expiry.
 *
 * @param {object} [options]
 * @param {import('react-router-dom').NavigateFunction} [options.navigate] - React Router navigate; uses replace to /signin
 * @param {boolean} [options.skipRedirect] - Skip navigation (rare; default false)
 * @param {string} [options.reason] - Log label for debugging
 */
export async function performLogout(options = {}) {
  const { navigate, skipRedirect = false, reason = 'manual' } = options;

  if (logoutInProgress) {
    return;
  }
  logoutInProgress = true;

  try {
    clearPostTakeoverGrace();

    // Stop WebSocket reconnect timers before clearing session (avoids stale-token handshake loops).
    try {
      const { default: websocketService } = await import('./websocketService');
      websocketService.disconnect();
    } catch (e) {
      console.warn('[performLogout] websocket disconnect failed:', e);
    }

    // 1. FCM
    await MessagingService.clearTokenOnLogout();

    // 2. Notifications + toast queue
    try {
      notificationHandlers.clearAllNotifications?.();
    } catch (e) {
      console.warn('[performLogout] clearAllNotifications failed:', e);
    }
    clearRealtimeNotificationToastQueue();

    // 3. Backend logout API + sessionStorage/localStorage clear (authService.logout finally)
    await authService.logout();

    // 3b. Clear in-memory React Query master-data cache (no cross-tenant/session leakage)
    try {
      queryClient.clear();
    } catch (e) {
      console.warn('[performLogout] queryClient.clear failed:', e);
    }

    // 4. React auth state
    try {
      authHandlers.setUser?.(null);
      authHandlers.setError?.(null);
    } catch (e) {
      console.warn('[performLogout] auth state reset failed:', e);
    }

    // 5. Global logout event — WebSocket, queue listeners, etc.
    window.dispatchEvent(new Event('user-logged-out'));

    // 7. Redirect (step 6 is handled by listeners on the event above)
    if (!skipRedirect) {
      if (typeof navigate === 'function') {
        navigate('/signin', { replace: true });
      } else if (typeof window !== 'undefined') {
        window.location.replace('/signin');
      }
    }
  } catch (err) {
    console.error(`[performLogout] error (${reason}):`, err);
    try {
      authHandlers.setUser?.(null);
      authHandlers.setError?.(null);
    } catch (_) {
      /* ignore */
    }
    window.dispatchEvent(new Event('user-logged-out'));
    if (!skipRedirect && typeof window !== 'undefined') {
      window.location.replace('/signin');
    }
  } finally {
    logoutInProgress = false;
  }
}

/**
 * 401 / unauthorized cleanup (no React Router navigate available).
 */
export async function performUnauthorizedLogout(reason = '401') {
  if (isSessionTakeoverInProgress() || isPostTakeoverGraceActive()) {
    console.info('[performUnauthorizedLogout] suppressed during session takeover/grace', { reason });
    return;
  }
  return performLogout({ reason, skipRedirect: false });
}
